/**
 * @file catalog-options.ts
 * @description Вид каталога и карточки товара (phase19.16 Р2а «Каталог и
 * товар»; db-design-r2a, ответы 1 и 5; uiux-design-r2a §2, §4; SB31, SB32,
 * SB62, SB65): шаблон карточки и её поля, сетка по устройствам, товаров на
 * странице, сортировки, фильтры, шапка категории, плитки подкатегорий,
 * подписи бейджей и вид карточки — как у всех карточек сайта или свой
 * (скругление и тень/рамка; SB31, дополнение 2026-10-11); как показать
 * баннер категории — фоном во всю ширину или картинкой сбоку, затемнение и
 * тон текста (решение пользователя 2026-10-12, по образцу обложки ленты блога).
 *
 * Хранится в `SiteDraft.catalogOptions` (JSON, `{}` по умолчанию) и
 * публикуется вместе с видом сайта в `SiteRevision.catalog.options` (часть
 * CATALOG, право контента, SB62). Карточка одна на сайт: каталог, «Похожие
 * товары» и элемент «Товары» читают её же. Цена на карточке показывается
 * всегда и не отключается (обязательная информация о товаре, ответ 5).
 * Фильтры — справа (решение 2026-03-22), место не настраивается.
 * Чтение (`readCatalogOptions`) не падает: недостающее — умолчанием, битое
 * поле — только оно само, перекрёстные правила приводятся в порядок.
 */

import { z } from "zod";

import { liftCatalogCardLookV1 } from "./legacy-v1.js";
import { cloneJson, repairBySchema, type RepairIssue } from "./repair.js";
import { mergeMissingKeys, siteCardsSchema, siteRadiusSchema } from "./schema.js";

// ── Справочники ──

/**
 * Сортировки каталога — те же ключи, что `CATALOG_SORTS` витрины
 * (apps/web/src/lib/catalog/api-catalog.ts) и параметр `?sort=` API.
 */
export const CATALOG_SORT_KEYS = ["none", "popular", "rating", "new", "price_asc", "price_desc", "alpha"] as const;
export type CatalogSort = (typeof CATALOG_SORT_KEYS)[number];

/** Подписи сортировок (как в сайдбаре витрины, catalog-url.ts). */
export const CATALOG_SORT_LABELS: Readonly<Record<CatalogSort, string>> = {
  none: "Без сортировки",
  popular: "Популярные",
  rating: "По рейтингу",
  new: "Последние поступления",
  price_asc: "По возрастанию стоимости",
  price_desc: "По убыванию стоимости",
  alpha: "По алфавиту А–Я",
};

/** Шаблоны карточки товара (ответ 5). */
export const PRODUCT_CARD_TEMPLATE_KEYS = ["classic", "overlay", "horizontal", "compact"] as const;
export type ProductCardTemplate = (typeof PRODUCT_CARD_TEMPLATE_KEYS)[number];

export interface ProductCardTemplateInfo {
  readonly key: ProductCardTemplate;
  /** Название в кабинете: «Классика». */
  readonly label: string;
  /** Для чего и чем отличается. */
  readonly hint: string;
  /** Ключ SVG-схемы превью в кабинете (окрашивается ролями магазина). */
  readonly previewKey: string;
}

/** Реестр шаблонов карточки (OCP: новый шаблон — новая запись и рендер на витрине). */
export const PRODUCT_CARD_TEMPLATES: readonly ProductCardTemplateInfo[] = [
  {
    key: "classic",
    label: "Классика",
    hint: "Фото сверху, под ним название, цена и кнопка",
    previewKey: "card-classic",
  },
  {
    key: "overlay",
    label: "Текст на фото",
    hint: "Название и цена поверх фото с затемнением",
    previewKey: "card-overlay",
  },
  {
    key: "horizontal",
    label: "Фото слева",
    hint: "Фото слева, текст справа; на ПК не больше 2 в ряд, на телефоне по одной",
    previewKey: "card-horizontal",
  },
  {
    key: "compact",
    label: "Компактная",
    hint: "Без короткого описания, кнопка значком — для длинных списков",
    previewKey: "card-compact",
  },
];

/** Бейджи, у которых можно сменить подпись (SB65; «Новинка» ставится сама, SB32). */
export const CATALOG_BADGE_KEYS = ["BESTSELLER", "NEW", "SALE", "COMING_SOON"] as const;
export type CatalogBadgeKey = (typeof CATALOG_BADGE_KEYS)[number];

/** Подписи бейджей платформы (как на витрине до Р2а). */
export const CATALOG_BADGE_DEFAULT_LABELS: Readonly<Record<CatalogBadgeKey, string>> = {
  BESTSELLER: "бестселлер",
  NEW: "новинки",
  SALE: "распродажа",
  COMING_SOON: "скоро в продаже",
};

/** Длина своей подписи бейджа, знаков. */
export const BADGE_LABEL_MAX = 24;

/**
 * SB65 (решение пользователя 2026-10-12): слова, которых не должно быть в
 * подписи бейджа — превосходная степень и рейтинги без подтверждения (закон
 * «О рекламе», ст. 5). `pattern` — по нижнему регистру с «ё» → «е».
 */
const BADGE_STOP_WORDS: readonly { readonly word: string; readonly pattern: RegExp }[] = [
  { word: "№", pattern: /№/ },
  { word: "#1", pattern: /#\s*1(?![0-9])/ },
  { word: "лучший", pattern: /лучш/ },
  { word: "самый", pattern: /(?:^|[^а-яa-z])сам(?:ый|ая|ое|ые|ого|ой|ую|ых|ым)(?![а-я])/ },
  { word: "топ", pattern: /(?:^|[^а-яa-z])(?:топ|top)(?![а-яa-z])/ },
  { word: "лидер", pattern: /лидер/ },
  { word: "единственный", pattern: /единственн/ },
  { word: "сезона", pattern: /(?:^|[^а-я])сезона(?![а-я])/ },
  { word: "месяца", pattern: /(?:^|[^а-я])месяца(?![а-я])/ },
];

/** Стоп-слова подписей бейджей для подсказки в кабинете. */
export const BADGE_LABEL_STOP_WORDS: readonly string[] = BADGE_STOP_WORDS.map((s) => s.word);

/**
 * Проверка своей подписи бейджа (SB65): null — можно; иначе — причина словами.
 * Одна функция для кабинета (подсказка у поля) и сервера (схема записи).
 */
export function badgeLabelIssue(label: string): string | null {
  const trimmed = label.trim();
  if (trimmed.length === 0) return "Впишите подпись бейджа";
  if (label.length > BADGE_LABEL_MAX) return `Не длиннее ${BADGE_LABEL_MAX} знаков`;
  const normalized = trimmed.toLowerCase().replace(/ё/g, "е");
  const hit = BADGE_STOP_WORDS.find((s) => s.pattern.test(normalized));
  return hit
    ? `В подписи бейджа нельзя «${hit.word}»: превосходные утверждения без подтверждения запрещены законом о рекламе`
    : null;
}

// ── Схема ──

const badgeLabel = z.string().superRefine((value, ctx) => {
  const issue = badgeLabelIssue(value);
  if (issue) ctx.addIssue({ code: "custom", message: issue });
});

/**
 * Вид карточки товара (SB31, дополнение 2026-10-11; числами — SB120 этап 2,
 * db-design-sb120-stage2 §1): site — как у всех карточек сайта
 * (theme.shapes.radius.card / cards), own — своё скругление карточки px и
 * свои рамка и тень (тот же вид, что `shapes.cards`); CSS — те же функции,
 * что у темы (css.ts, productCardCssVars). Различитель здесь — `mode`, у
 * цвета рамки — `kind`. Ступени v1 поднимает `liftCatalogCardLookV1` при
 * чтении (readCatalogOptions) и записи (мост `mergeCatalogOptions` в api).
 */
export const CATALOG_CARD_LOOK_MODES = ["site", "own"] as const;
export type CatalogCardLookMode = (typeof CATALOG_CARD_LOOK_MODES)[number];
export const CATALOG_CARD_LOOK_LABEL = "Вид карточки";
export const CATALOG_CARD_LOOK_MODE_OPTIONS: Readonly<Record<CatalogCardLookMode, string>> = {
  site: "Как у всех карточек сайта",
  own: "Свои",
};

export const catalogCardLookSchema = z.discriminatedUnion("mode", [
  z.strictObject({ mode: z.literal("site") }),
  z.strictObject({
    mode: z.literal("own"),
    /** Скругление карточки, px 0–40 (v1 none / soft / large → 0 / 8 / 20). */
    radius: siteRadiusSchema.shape.card,
    cards: siteCardsSchema,
  }),
]);
export type CatalogCardLook = z.infer<typeof catalogCardLookSchema>;

/** Поля без перекрёстных правил (для починки по полям). */
export const siteCatalogOptionsShapeSchema = z.strictObject({
  card: z.strictObject({
    template: z.enum(PRODUCT_CARD_TEMPLATE_KEYS),
    imageAspect: z.enum(["1:1", "4:5", "3:4"]),
    /** Что показывать на карточке; цена — всегда (не настраивается). */
    fields: z.strictObject({
      badge: z.boolean(),
      shortDescription: z.boolean(),
      rating: z.boolean(),
      /** Фасовка «от 50 г»; при нескольких фасовках кнопка — «Выбрать фасовку». */
      skuPicker: z.boolean(),
    }),
    buyButton: z.enum(["icon", "text", "none"]),
    /** Нет ключа в старых данных — «как у всех карточек сайта». */
    look: catalogCardLookSchema,
  }),
  grid: z.strictObject({
    desktop: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    tablet: z.union([z.literal(2), z.literal(3)]),
    phone: z.union([z.literal(1), z.literal(2)]),
  }),
  perPage: z.union([z.literal(12), z.literal(24), z.literal(36), z.literal(48)]),
  sorts: z.strictObject({
    default: z.enum(CATALOG_SORT_KEYS),
    /** Сортировки в списке покупателя; хотя бы одна, включает `default`. */
    enabled: z.array(z.enum(CATALOG_SORT_KEYS)).min(1, "Оставьте хотя бы одну сортировку").max(CATALOG_SORT_KEYS.length),
  }),
  /** Группы фильтров справа. */
  filters: z.strictObject({ tags: z.boolean(), categoryTree: z.boolean() }),
  /** Шапка страницы категории: заголовок, заголовок с описанием, баннер из фото категории. */
  categoryHeader: z.enum(["title", "title-description", "banner"]),
  /**
   * Как показать баннер категории (решение пользователя 2026-10-12; тот же
   * выбор, что у обложки ленты блога `coverLayout`/`coverDim`/`coverTextTone`
   * в blog-options.ts). Действует только при `categoryHeader: "banner"`.
   * Точки фокуса здесь нет: она своя у каждой категории и берётся из фото
   * категории (`Category.imageFocus`, Р2а), а не из вида каталога.
   */
  categoryBanner: z.strictObject({
    /**
     * background — фоном во всю ширину под заголовком (как было до решения);
     * side-right / side-left — картинкой сбоку от текста.
     */
    layout: z.enum(["background", "side-right", "side-left"]),
    /**
     * Затемнение фото в режиме «фоном», доля 0–0,6 — диапазон как у `dim`
     * фото-фона секции и обложки ленты блога (один ползунок на всё).
     */
    dim: z.number().min(0).max(0.6),
    /** Тон текста поверх фото в режиме «фоном»; в режимах «сбоку» не действует. */
    textTone: z.enum(["light", "dark"]),
  }),
  /** Плитки подкатегорий над списком: фото категории квадратом (решение 2026-10-12). */
  subcategoryTiles: z.strictObject({
    on: z.boolean(),
    look: z.enum(["overlay", "below"]),
    phone: z.enum(["scroll", "grid2"]),
  }),
  /** Свои подписи бейджей (SB65); нет ключа — подпись платформы. */
  badgeLabels: z.strictObject({
    BESTSELLER: badgeLabel.optional(),
    NEW: badgeLabel.optional(),
    SALE: badgeLabel.optional(),
    COMING_SOON: badgeLabel.optional(),
  }),
});

type CatalogOptionsShape = z.infer<typeof siteCatalogOptionsShapeSchema>;

/** Перекрёстные правила (ответ 5, uiux-design-r2a §2). */
function checkCatalogOptions(o: CatalogOptionsShape, ctx: z.RefinementCtx): void {
  if (o.card.template === "horizontal" && o.grid.desktop > 2) {
    ctx.addIssue({
      code: "custom",
      path: ["grid", "desktop"],
      message: "Карточка «Фото слева» — не больше 2 в ряд на ПК",
    });
  }
  if (o.card.template === "compact" && o.card.fields.shortDescription) {
    ctx.addIssue({
      code: "custom",
      path: ["card", "fields", "shortDescription"],
      message: "У компактной карточки нет короткого описания",
    });
  }
  if (new Set(o.sorts.enabled).size !== o.sorts.enabled.length) {
    ctx.addIssue({ code: "custom", path: ["sorts", "enabled"], message: "Сортировка отмечена дважды" });
  }
  if (!o.sorts.enabled.includes(o.sorts.default)) {
    ctx.addIssue({
      code: "custom",
      path: ["sorts", "default"],
      message: "Сортировка по умолчанию должна быть в списке включённых",
    });
  }
}

/** Строгая схема для записи (кабинет → сервер). */
export const siteCatalogOptionsSchema = siteCatalogOptionsShapeSchema.superRefine(checkCatalogOptions);

export type SiteCatalogOptions = z.infer<typeof siteCatalogOptionsSchema>;

const deepFreeze = <T>(value: T): T => {
  if (typeof value === "object" && value !== null) {
    for (const nested of Object.values(value)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
};

/**
 * Умолчание платформы — как витрина рисовала каталог до Р2а (принцип «Как
 * было», сверено с HEAD apps/web 2026-10-12): CatalogProductCard — фото сверху
 * почти квадратом, бейдж, фасовка, рейтинг, кнопка текстом, без короткого
 * описания; сетка 1 / 2 (sm) / 3 (xl); 24 товара на странице; «Без
 * сортировки» по умолчанию и все 7 сортировок; фильтры — дерево и метки.
 * Шапка категории — заголовок с описанием (решение пользователя 2026-10-12;
 * баннер — по выбору). Плиток подкатегорий на витрине не было — выключены.
 */
export const DEFAULT_SITE_CATALOG_OPTIONS: Readonly<SiteCatalogOptions> = deepFreeze<SiteCatalogOptions>({
  card: {
    template: "classic",
    imageAspect: "1:1",
    fields: { badge: true, shortDescription: false, rating: true, skuPicker: true },
    buyButton: "text",
    look: { mode: "site" },
  },
  grid: { desktop: 3, tablet: 2, phone: 1 },
  perPage: 24,
  sorts: { default: "none", enabled: [...CATALOG_SORT_KEYS] },
  filters: { tags: true, categoryTree: true },
  categoryHeader: "title-description",
  // Как было до 2026-10-12: баннер фоном во всю ширину, светлый текст; ровного
  // затемнения не было (витрина рисует градиент снизу под текстом), поэтому
  // умолчание dim 0 — существующие баннеры после выкладки не темнеют (ревью
  // 2026-10-12). Ступени в кабинете 0/20/40/60 как у обложки ленты блога. Нет
  // ключа в старых данных — эти умолчания (mergeMissingKeys по полям).
  categoryBanner: { layout: "background", dim: 0, textTone: "light" },
  subcategoryTiles: { on: false, look: "below", phone: "grid2" },
  badgeLabels: {},
});

export interface SiteCatalogOptionsReadResult {
  options: SiteCatalogOptions;
  replaced: RepairIssue[];
}

/** Перекрёстные правила при чтении — к ближайшему допустимому. */
function fixCatalogRules(o: CatalogOptionsShape, replaced: RepairIssue[]): CatalogOptionsShape {
  const result = cloneJson(o);
  if (result.card.template === "horizontal" && result.grid.desktop > 2) {
    result.grid.desktop = 2;
    replaced.push({ path: "grid.desktop", reason: "Карточка «Фото слева» — 2 в ряд на ПК" });
  }
  if (result.card.template === "compact" && result.card.fields.shortDescription) {
    result.card.fields.shortDescription = false;
    replaced.push({ path: "card.fields.shortDescription", reason: "У компактной карточки нет короткого описания — снято" });
  }
  const enabled = [...new Set(result.sorts.enabled)];
  if (enabled.length !== result.sorts.enabled.length) {
    replaced.push({ path: "sorts.enabled", reason: "Повтор сортировки снят" });
  }
  if (!enabled.includes(result.sorts.default)) {
    enabled.unshift(result.sorts.default);
    replaced.push({ path: "sorts.enabled", reason: "Сортировка по умолчанию добавлена в список" });
  }
  result.sorts.enabled = enabled;
  return result;
}

/**
 * Параметры каталога с видом карточки v1 → v2 (SB120 этап 2,
 * db-design-sb120-stage2 §2): `card.look` со ступенями — числами. Вход не
 * меняется. Нужен до `mergeMissingKeys` при чтении (иначе починка по полям
 * заменила бы «Свои» на «как у всех») и после слияния частичной правки в api
 * (`mergeCatalogOptions`: старая вкладка шлёт `{ look: { radius: "soft" } }`).
 * `lifted` — пути для журнала моста записи.
 */
export function liftCatalogOptionsV1(raw: unknown): { options: unknown; lifted: string[] } {
  const options = cloneJson(raw);
  if (typeof options !== "object" || options === null || Array.isArray(options)) return { options, lifted: [] };
  const card = (options as Record<string, unknown>)["card"];
  if (typeof card !== "object" || card === null || Array.isArray(card) || !("look" in card)) {
    return { options, lifted: [] };
  }
  const cardRecord = card as Record<string, unknown>;
  const { look, lifted } = liftCatalogCardLookV1(cardRecord["look"]);
  cardRecord["look"] = look;
  return { options, lifted };
}

/** Чтение `SiteDraft.catalogOptions` / `SiteRevision.catalog.options`: недостающее — умолчанием, битое — по полям. */
export function readCatalogOptions(raw: unknown): SiteCatalogOptionsReadResult {
  const replaced: RepairIssue[] = [];
  const defaults = cloneJson(DEFAULT_SITE_CATALOG_OPTIONS) as SiteCatalogOptions;
  try {
    const merged = mergeMissingKeys(defaults, liftCatalogOptionsV1(raw ?? {}).options);
    const direct = siteCatalogOptionsSchema.safeParse(merged);
    if (direct.success) return { options: direct.data, replaced };
    const shape = siteCatalogOptionsShapeSchema.safeParse(
      repairBySchema(siteCatalogOptionsShapeSchema, merged, defaults, "", replaced),
    );
    if (shape.success) {
      const fixed = siteCatalogOptionsSchema.safeParse(fixCatalogRules(shape.data, replaced));
      if (fixed.success) return { options: fixed.data, replaced };
    }
  } catch {
    // Чтение не роняет витрину: ниже — умолчания.
  }
  replaced.push({ path: "", reason: "Параметры каталога не прошли проверку — взяты умолчания" });
  return { options: defaults, replaced };
}
